import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, Col, Row, Form, Label, Input, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { get, putJson } from '../../hook/request';
import { Btn, H3 } from '../../AbstractElements';


type ApiUser = {
  ID: number;
  typeID: number | string;
  codice: string | null;
  email: string;
  cognome: string | null;
  nome: string | null;
  nascita_data: string | null; 
  ragionesociale: string | null;
  piva: string | null;
  cf: string | null;
};

type FormState = {
  typeID: string;
  codice: string; 
  email: string;  
  cognome: string;
  nome: string;
  data_nascita: string; 
  ragione_sociale: string;
  partita_iva: string;
  codice_fiscale: string;
};



export default function UserProfileForm({ userId = 1 }: { userId?: number }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<FormState>({
    typeID: '',
    codice: '',
    email: '',
    cognome: '',
    nome: '',
    data_nascita: '',
    ragione_sociale: '',
    partita_iva: '',
    codice_fiscale: '',
  });


  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await get<ApiUser | any>(`/users/detail`);
        const data: any = (res as any)?.data ?? res; // axios/plain
        const u: ApiUser = data as ApiUser;
        setForm({
          typeID: String(u.typeID ?? ''),
          codice: u.codice ?? '',
          email: u.email ?? '',
          cognome: u.cognome ?? '',
          nome: u.nome ?? '',
          data_nascita: (u.nascita_data ?? '').slice(0, 10),
          ragione_sociale: u.ragionesociale ?? '',
          partita_iva: u.piva ?? '',
          codice_fiscale: u.cf ?? '',
        });
      } catch (e: any) {
        toast.error(e?.message || 'Errore nel caricamento profilo');
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
  };

  const canSubmit = useMemo(() => !!form.nome.trim() && !!form.cognome.trim(), [form]);

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (!canSubmit) return toast.warn('Compila i campi obbligatori');

    const payload = {
      typeID: Number(form.typeID || 1),
      codice: form.codice || null,
      email: form.email,
      cognome: form.cognome,
      nome: form.nome,
      data_nascita: form.data_nascita || null,
      ragione_sociale: form.ragione_sociale || null,
      partita_iva: form.partita_iva || null,
      codice_fiscale: form.codice_fiscale || null,
      user_email: form.email,
    };

    try {
      setSaving(true);
      await putJson(`/users/update?ID=${userId}`, payload);
      toast.success('Profilo aggiornato');
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Errore salvataggio';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardBody className="d-flex justify-content-center py-5"><Spinner /></CardBody>
      </Card>
    );
  }

  return (
    <div className="page-body pt-4">
      <Card>
        <CardBody>
          <H3 className="mb-3">Gestione Profilo</H3>
          <Form onSubmit={onSubmit}>
            {/* Profilo */}
            <h6 className="mb-2">Profilo</h6>
            <Row className="g-3">
              <Col md={6}>
                <Label>Codice</Label>
                <Input name="codice" value={form.codice} onChange={onChange} placeholder="Codice" disabled />
              </Col>
              <Col md={6}>
                <Label>Email</Label>
                <Input type="email" name="email" value={form.email} onChange={onChange} placeholder="email" disabled />
              </Col>
            </Row>


            <h6 className="mt-4 mb-2">Anagrafica</h6>
            <Row className="g-3">
              <Col md={6}>
                <Label>Cognome</Label>
                <Input name="cognome" value={form.cognome} onChange={onChange} />
              </Col>
              <Col md={6}>
                <Label>Nome</Label>
                <Input name="nome" value={form.nome} onChange={onChange} />
              </Col>
              <Col md={6}>
                <Label>Data di nascita</Label>
                <Input type="date" name="data_nascita" value={form.data_nascita} onChange={onChange} />
              </Col>
              <Col md={6}>
                <Label>Ragione sociale</Label>
                <Input name="ragione_sociale" value={form.ragione_sociale} onChange={onChange} />
              </Col>
              <Col md={6}>
                <Label>Partita IVA</Label>
                <Input name="partita_iva" value={form.partita_iva} onChange={onChange} />
              </Col>
              <Col md={6}>
                <Label>Codice Fiscale</Label>
                <Input name="codice_fiscale" value={form.codice_fiscale} onChange={onChange} />
              </Col>
            </Row>

            <div className="mt-4">
              <Btn color="primary" className="px-4" type="submit" disabled={!canSubmit || saving}>
                {saving ? <Spinner size="sm" /> : 'Aggiorna'}
              </Btn>
            </div>
          </Form>
        </CardBody>
      </Card>
    </div>

  );
}
